import { useTranslation } from "react-i18next";

import { useProfile } from "@/entities/profile";
import { useUpdateProfile } from "@/features/profile-settings";
import { useToast } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Приватность в публичных рейтингах (доработки §5).
 *
 * Отдельный экран, а не строка на странице уведомлений: согласию нужен
 * объясняющий текст. Тумблер «показывать имя» без ответа на вопрос «кому и
 * где» — тёмный паттерн, а поместить этот ответ в строку меню некуда.
 *
 * Второй абзац подсказки — про то, что результаты учитываются в любом случае.
 * Без него выключенный тумблер читается как «меня выкинут из рейтинга», и
 * человек соглашается на публичность, чтобы не потерять место, — то есть
 * ровно то согласие, которого §5 требует избегать.
 */
export function ProfilePrivacyPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isPending, isError, refetch } = useProfile();
  const update = useUpdateProfile();

  if (isPending) return <ScreenSkeleton blocks={[88, 96]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  const enabled = data.user.public_rating_opt_in;

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("profile.menu.privacy")} subtitle={t("profile.privacy.subtitle")} />

      <label
        data-haptic="select"
        className="press-row flex items-center justify-between gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-4"
      >
        <span className="flex flex-col gap-0.5">
          <span className="text-label font-semibold text-text-primary">
            {t("profile.privacy.label")}
          </span>
          <span className="text-caption font-normal text-text-secondary">
            {t("profile.privacy.hint")}
          </span>
        </span>
        <input
          type="checkbox"
          role="switch"
          checked={enabled}
          disabled={update.isPending}
          onChange={(event) => {
            update.mutate(
              { public_rating_opt_in: event.target.checked },
              {
                onError: () => {
                  toast.show({ tone: "loss", title: t("states.error.title") });
                },
              },
            );
          }}
          className="size-6 shrink-0 accent-action-primary"
        />
      </label>

      <p className="text-caption font-normal text-text-tertiary">
        {enabled ? t("profile.privacy.noteOn") : t("profile.privacy.noteOff")}
      </p>
    </div>
  );
}
